import React, { PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';
import * as S from './ModalDrawer.styles';
import { useResponsive } from '@/hooks/useResponsive';
import { Button } from '@/components/UI/Button/Button';
import { AngleBackIcon, CloseIcon } from '@/components/UI/Icons/Icons';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  DrawerTitleContent,
  isDrawerTitle,
} from '@/components/Drawer/Drawer.types';

interface ModalDrawerProps {
  dataTestId?: string;
  isOpen: boolean;
  onClose: () => void;
  title: DrawerTitleContent;
  subtitle?: string | JSX.Element;
  bottom?: React.ReactNode;
  canGoBack?: boolean;
  onBack?: () => void;
  isBodyScrollLocked?: boolean;
  isInPortal?: boolean;
  maxWidth?: number;
  background?: string;
  footerPaddingBottom?: number;
}

export const ModalDrawer = ({
  dataTestId,
  isOpen,
  onClose,
  title,
  children,
  subtitle,
  bottom,
  canGoBack = false,
  onBack,
  isBodyScrollLocked = false,
  isInPortal = false,
  maxWidth = 600,
  background,
  footerPaddingBottom = 30,
}: PropsWithChildren<ModalDrawerProps>) => {
  const { isDesktop } = useResponsive();
  const theme = useTheme();
  const hasBackAction = canGoBack || Boolean(onBack);
  const closePosition = hasBackAction ? 'left' : 'right';

  if (isDesktop) {
    return (
      <S.Modal
        isInPortal={isInPortal}
        isScrollLocked={isBodyScrollLocked}
        dataTestId={dataTestId}
        isOpen={isOpen}
        onClose={onClose}
        maxWidth={maxWidth}
        background={background ?? theme.palette.secondary.background}
      >
        {title && (
          <S.ModalHeader>
            {hasBackAction && (
              <S.GoBackButton
                appearance="transparent"
                hasPadding={false}
                onClick={onBack ?? onClose}
              >
                <S.GoBackIcon />
              </S.GoBackButton>
            )}
            {React.isValidElement(title) ? (
              title
            ) : (
              <S.ModalTitle $isCentered={hasBackAction}>
                {typeof title === 'string' ? (
                  title
                ) : isDrawerTitle(title) ? (
                  <>
                    <img
                      src={title.iconSrc}
                      alt=""
                      width={20}
                      height={17}
                      loading="lazy"
                    />
                    {title.text}
                  </>
                ) : (
                  title
                )}
              </S.ModalTitle>
            )}
            {(!canGoBack || onBack) && (
              <Button
                appearance="transparent"
                hasPadding={false}
                onClick={onClose}
              >
                <CloseIcon
                  width={20}
                  height={20}
                  fill={theme.palette.secondary.labels}
                />
              </Button>
            )}
          </S.ModalHeader>
        )}

        <S.ModalContent>
          {subtitle}
          {children}
        </S.ModalContent>

        <S.ModalFooter $paddingBottom={footerPaddingBottom}>
          {bottom}
        </S.ModalFooter>
      </S.Modal>
    );
  }

  return (
    <Drawer
      isInPortal={isInPortal}
      isHeadFixed={false}
      dataTestId={dataTestId}
      hasBottomMargin={false}
      isOpen={isOpen}
      close={onClose}
      title={title}
      subtitle={subtitle}
      {...(hasBackAction && {
        left: { icon: <AngleBackIcon />, onClick: onBack ?? onClose },
      })}
      bottom={bottom}
      className="action"
      hasBottomBorder={false}
      closePosition={closePosition}
      background={background}
    >
      {children}
    </Drawer>
  );
};
